import React from "react";
import { useAppDispatch } from "../redux/hooks";
import {
  fetchBillingRestriction,
  setBusinessId
} from "../redux/slices/businessAccessSlice";

function getBusinessIdFromLocalStorage(): string | undefined {
  try {
    const raw = localStorage?.getItem("currentBusiness");
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed?.BusinessId || parsed?.businessId;
  } catch {
    return undefined;
  }
}

/**
 * Keeps Redux in sync with the currently selected business (from localStorage)
 * and triggers billing/restriction fetch whenever businessId changes.
 *
 * Put this hook once at app root (e.g. in App.tsx).
 */
export function useBusinessAccessSync() {
  const dispatch = useAppDispatch();
  const lastBusinessIdRef = React.useRef<string | undefined>(undefined);

  React.useEffect(() => {
    const sync = () => {
      const businessId = getBusinessIdFromLocalStorage();
      if (businessId !== lastBusinessIdRef.current) {
        lastBusinessIdRef.current = businessId;
        dispatch(setBusinessId(businessId));
        if (businessId) dispatch(fetchBillingRestriction(String(businessId)));
      }
    };

    // initial
    sync();

    // storage event fires only for other tabs/windows
    const onStorage = (e: StorageEvent) => {
      if (e.key === "currentBusiness") sync();
    };
    window.addEventListener("storage", onStorage);

    // same-tab updates won't fire storage, so poll lightly
    const t = window.setInterval(sync, 1000);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearInterval(t);
    };
  }, [dispatch]);
}


